import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../../lib/firebase';
import { supabase } from '../../../lib/supabaseClient';
import VerifiedBadge from '../../verified/VerifiedBadge';
import { 
  onAuthStateChanged, 
  User as FirebaseUser
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  orderBy, 
  limit,
  getDocs
} from 'firebase/firestore';
import { 
  ArrowLeft, 
  Home, 
  ShoppingBag, 
  Package, 
  Eye, 
  Heart, 
  TrendingUp, 
  DollarSign,
  Users,
  MessageSquare,
  MessagesSquare,
  Settings,
  Plus,
  Grid,
  BarChart3,
  User as UserIcon,
  Bell,
  CheckCircle,
  MapPin,
  Clock,
  Shield,
  CreditCard,
  ShoppingCart,
  Calendar,
  Filter,
  Search,
  X,
  ChevronRight,
  Menu,
  Store,
  Building,
  BarChart2,
  Wallet,
  Banknote,
  Target,
  Percent,
  Layers,
  Box,
  AlertCircle,
  PackageCheck,
  Truck,
  FileText,
  PieChart,
  LineChart,
  Activity,
  Users as UsersIcon,
  Globe,
  Tag,
  Image,
  Edit,
  Trash2,
  MoreVertical,
  ExternalLink,
  List,
  Grid3x3,
  Check,
  XCircle,
  Truck as TruckIcon,
  ClipboardCheck,
  RefreshCw,
  BellDot,
  PackageX,
  RotateCcw,
  Download,
  Upload,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
  Square,
  SquareStack,
  Star,
  Phone
} from 'lucide-react';
import { notificationService } from '../../../services/notificationService';
import { AdsPromoteButton, MyAdsButton } from '../../ads/Ads';
import './VendorDashboard.css';

// ========== IMAGE TRANSFORM HELPERS ==========
const getThumbnailUrl = (originalUrl: string, width = 400, quality = 65): string => {
  if (!originalUrl) return originalUrl;
  if (!originalUrl.includes('/storage/v1/object/public/')) return originalUrl;
  const transformed = originalUrl.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );
  const sep = transformed.includes('?') ? '&' : '?';
  return `${transformed}${sep}width=${width}&quality=${quality}&resize=cover`;
};

const getOriginalUrl = (url: string): string => {
  if (!url) return url;
  if (url.includes('/storage/v1/render/image/public/')) {
    const cleaned = url.replace('/storage/v1/render/image/public/', '/storage/v1/object/public/');
    return cleaned.split('?')[0];
  }
  return url;
};
// ========== END IMAGE HELPERS ==========

// ========== NOTIFICATION FUNCTIONS ==========
const sendOrderStatusNotification = async (
  userId: string,
  shopName: string,
  orderNumber: string,
  productNames: string,
  action: 'accepted' | 'rejected'
) => {
  try {
    console.log('📢 Sending notification to customer:', userId);
    
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('email, name')
      .eq('firebase_uid', userId)
      .single();

    if (userError) {
      console.error('❌ Error fetching user:', userError);
    }

    const actionText = action === 'accepted' ? 'accepted' : 'rejected';
    
    const notificationBody = `${shopName} ${actionText} your order of ${productNames}`;
    const notificationTitle = `📦 Order ${action === 'accepted' ? 'Accepted' : 'Rejected'}!! ${shopName}`;

    const notificationData: any = {
      title: notificationTitle,
      body: notificationBody,
      notification_type: 'order',
      redirect_url: '/user/orders',
      data: {
        userId: userId,
        shopName: shopName,
        orderNumber: orderNumber,
        productNames: productNames,
        action: action,
        type: 'order_status_update',
        timestamp: new Date().toISOString()
      }
    };

    notificationData.target_user_id = userId;

    const SPARE_EMAIL = 'miracleglory2099@gmail.com';
    if (userData?.email) {
      notificationData.email = userData.email;
    } else {
      notificationData.email = SPARE_EMAIL;
    }

    const response = await notificationService.sendNotification(notificationData);
    
    if (response && response.success) {
      const { error: insertError } = await supabase
        .from('user_notifications')
        .insert([{
          user_id: userId,
          title: notificationTitle,
          message: notificationBody,
          type: 'order_update',
          data: notificationData.data,
          is_read: false,
          created_at: new Date().toISOString()
        }]);

      if (insertError) {
        console.error('❌ Error creating user_notification:', insertError);
      }
    }

  } catch (error) {
    console.error('❌ Error sending customer notification:', error);
  }
};

const extractProductNames = (items: any[]): string => {
  if (!items || items.length === 0) return 'items';
  if (items.length === 1) return items[0].product_title || 'product';
  if (items.length === 2) return `${items[0].product_title} and ${items[1].product_title}`;
  const firstTwo = items.slice(0, 2).map(i => i.product_title).join(', ');
  return `${firstTwo} and ${items.length - 2} other items`;
};
// ========== END NOTIFICATION FUNCTIONS ==========

// ========== LOCATION NAME FETCHING HELPERS ==========
const fetchStateName = async (stateId: number): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('states')
      .select('name')
      .eq('id', stateId)
      .single();
    
    if (error || !data) return `State ID: ${stateId}`;
    return data.name;
  } catch {
    return `State ID: ${stateId}`;
  }
};

const fetchUniversityName = async (universityId: number): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('universities')
      .select('name')
      .eq('id', universityId)
      .single();
    
    if (error || !data) return `University ID: ${universityId}`;
    return data.name;
  } catch {
    return `University ID: ${universityId}`;
  }
};

const fetchCampusName = async (campusId: number): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('campuses')
      .select('name')
      .eq('id', campusId)
      .single();
    
    if (error || !data) return `Campus ID: ${campusId}`;
    return data.name;
  } catch {
    return `Campus ID: ${campusId}`;
  }
};

const formatLocationWithNames = async (locationDetails: any): Promise<any> => {
  if (!locationDetails) return null;
  
  const formattedLocation = { ...locationDetails };
  
  if (locationDetails.state_id && (!locationDetails.state_name || locationDetails.state_name.includes('ID:'))) {
    formattedLocation.state_name = await fetchStateName(locationDetails.state_id);
  }
  
  if (locationDetails.university_id && (!locationDetails.university_name || locationDetails.university_name.includes('ID:'))) {
    formattedLocation.university_name = await fetchUniversityName(locationDetails.university_id);
  }
  
  if (locationDetails.campus_id && (!locationDetails.campus_name || locationDetails.campus_name.includes('ID:'))) {
    formattedLocation.campus_name = await fetchCampusName(locationDetails.campus_id);
  }
  
  const locationParts = [
    formattedLocation.state_name,
    formattedLocation.university_name,
    formattedLocation.campus_name,
    locationDetails.precise_location
  ].filter(Boolean);
  
  formattedLocation.full_location = locationParts.join(', ');
  
  return formattedLocation;
};
// ========== END LOCATION NAME FETCHING HELPERS ==========

interface VendorProfile {
  id: string;
  userId: string;
  shopName: string;
  profileImage: string;
  coverImage: string;
  bio: string;
  location: string;
  contactPhone: string;
  businessEmail: string;
  website: string;
  instagramHandle: string;
  facebookPage: string;
  twitterHandle: string;
  businessHours: string;
  returnPolicy: string;
  shippingPolicy: string;
  isActive: boolean;
  onboardingCompleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Product {
  id: string;
  title: string;
  price: number;
  original_price?: number;
  currency: string;
  images: string[];
  description: string;
  detailed_description?: string;
  category: string;
  subcategory?: string;
  condition: string;
  vendor_id: string;
  vendor_name: string;
  views_count: number;
  likes_count: number;
  sales_count: number;
  inventory: number;
  is_active: boolean;
  is_promoted: boolean;
  created_at: string;
  updated_at: string;
  tags: string[];
  specifications?: Record<string, string>;
  shipping_info?: {
    weight?: number;
    dimensions?: string;
    shipping_cost?: number;
    free_shipping_threshold?: number;
    estimated_delivery?: string;
  };
  warranty_info?: {
    has_warranty: boolean;
    warranty_period?: string;
    warranty_details?: string;
  };
  delivery_campus_ids?: string[];
}

interface OrderItem {
  id: string;
  order_id: string;
  product_id: string;
  vendor_id: string;
  product_title: string;
  vendor_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  status: string;
  location_details: any;
  created_at: string;
  updated_at: string;
  user_status: string;
  vendor_status: string;
  rejection_reason?: string;
}

interface Transaction {
  id: string;
  order_id: string;
  order_number: string;
  product_id: string;
  user_id: string;
  user_name: string;
  vendor_id: string;
  vendor_name: string;
  transaction_type: string;
  amount: number;
  user_old_balance: number;
  user_new_balance: number;
  vendor_old_balance: number;
  vendor_new_balance: number;
  user_status_at_time: string;
  vendor_status_at_time: string;
  description: string;
  rejection_reason?: string;
  metadata: any;
  order_created_at: string;
  transaction_created_at: string;
}

interface VendorBusiness {
  id: string;
  shopName: string;
  profileImage: string;
  isActive: boolean;
}

interface UserProfile {
  id: string;
  email: string;
  name: string;
  profileImage?: string;
  phone?: string;
}

interface OrderStats {
  total: number;
  pending: number;
  accepted: number;
  rejected: number;
  cancelled: number;
  received: number;
}

interface Review {
  id: string;
  product_id: string;
  vendor_id: string;
  user_id: string;
  user_name: string;
  rating: number;
  review_text: string;
  order_item_id: string;
  created_at: string;
}

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const VendorDashboard: React.FC = () => {
  const [vendorProfile, setVendorProfile] = useState<VendorProfile | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [vendorBusinesses, setVendorBusinesses] = useState<VendorBusiness[]>([]);
  const [activeTab, setActiveTab] = useState('products');
  const [loading, setLoading] = useState(true);
  const [showShopPopup, setShowShopPopup] = useState(false);
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [supabaseStats, setSupabaseStats] = useState<{
    followers_count: number;
    likes_count: number;
    sales_count: number;
    total_revenue: number;
    pending_balance: number;
    available_balance: number;
    virtual_account: string;
  } | null>(null);
  const [orderStats, setOrderStats] = useState<OrderStats>({
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    cancelled: 0,
    received: 0
  });
  const [processingOrder, setProcessingOrder] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedShopId, setSelectedShopId] = useState<string | null>(() => {
    return localStorage.getItem('lastSelectedShopId');
  });
  const [reviews, setReviews] = useState<Review[]>([]);
  
  const [showOrderInfoModal, setShowOrderInfoModal] = useState<{show: boolean, orderItem?: OrderItem, buyerPhone?: string, formattedLocation?: any}>({show: false});

  const navigate = useNavigate();

  const fetchUserProfile = async (userId: string) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserProfile({
          id: userId,
          email: userData.email || '',
          name: userData.name || 'User',
          profileImage: userData.profileImage || '',
          phone: userData.phone || ''
        });
      } else {
        setUserProfile({
          id: userId,
          email: currentUser?.email || '',
          name: currentUser?.displayName || 'User',
          profileImage: currentUser?.photoURL || '',
          phone: ''
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUserProfile({
        id: userId,
        email: currentUser?.email || '',
        name: currentUser?.displayName || 'User',
        profileImage: currentUser?.photoURL || '',
        phone: ''
      });
    }
  };

  const fetchBuyerPhone = async (userId: string): Promise<string> => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('phone')
        .eq('firebase_uid', userId)
        .single();

      if (error) {
        console.error('Error fetching buyer phone:', error);
        return 'Not available';
      }

      return data?.phone || 'Not available';
    } catch (error) {
      console.error('Error fetching buyer phone:', error);
      return 'Not available';
    }
  };

  const handleViewOrderInfo = async (orderItem: OrderItem) => {
    let buyerPhone = 'Not available';
    let formattedLocation = null;
    
    try {
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('user_id')
        .eq('id', orderItem.order_id)
        .single();

      if (!orderError && orderData?.user_id) {
        buyerPhone = await fetchBuyerPhone(orderData.user_id);
      }
    } catch (error) {
      console.error('Error fetching buyer info:', error);
    }
    
    if (orderItem.location_details) {
      formattedLocation = await formatLocationWithNames(orderItem.location_details);
    }
    
    setShowOrderInfoModal({show: true, orderItem, buyerPhone, formattedLocation});
  };

  const handleVisitAndCopyShop = async () => {
    if (!vendorProfile?.id) return;

    const shopUrl = `${window.location.origin}/vendor/${vendorProfile.id}`;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shopUrl);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = shopUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      
      const toast = document.createElement('div');
      toast.textContent = '✅ Shop link copied to clipboard!';
      toast.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #10b981;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        font-weight: 600;
        font-size: 14px;
        z-index: 99999;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      `;
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 2500);
      
    } catch (err) {
      console.error('❌ Copy failed:', err);
    }

    navigate(`/vendor/${vendorProfile.id}`);
  };

  const fetchSupabaseStats = async (vendorId: string) => {
    try {
      const { data, error } = await supabase
        .from('vendor_profiles')
        .select('followers_count, likes_count, sales_count, total_revenue, pending_balance, available_balance, virtual_account')
        .eq('vendor_id', vendorId)
        .single();

      if (error) throw error;

      setSupabaseStats({
        followers_count: data.followers_count || 0,
        likes_count: data.likes_count || 0,
        sales_count: data.sales_count || 0,
        total_revenue: data.total_revenue || 0,
        pending_balance: data.pending_balance || 0,
        available_balance: data.available_balance || 0,
        virtual_account: data.virtual_account || `VND${Date.now().toString().slice(8)}`
      });
    } catch (error) {
      console.error('Error fetching supabase stats:', error);
      setSupabaseStats({
        followers_count: 0,
        likes_count: 0,
        sales_count: 0,
        total_revenue: 0,
        pending_balance: 0,
        available_balance: 0,
        virtual_account: `VND${Date.now().toString().slice(8)}`
      });
    }
  };

  const fetchOrders = async (vendorId: string) => {
    try {
      const { data: orderItems, error } = await supabase
        .from('order_items')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setOrders(orderItems || []);

      const stats: OrderStats = {
        total: orderItems?.length || 0,
        pending: orderItems?.filter(item => 
          item.vendor_status === 'pending' && item.user_status !== 'cancelled'
        ).length || 0,
        accepted: orderItems?.filter(item => item.vendor_status === 'accepted').length || 0,
        rejected: orderItems?.filter(item => item.vendor_status === 'rejected').length || 0,
        cancelled: orderItems?.filter(item => item.user_status === 'cancelled').length || 0,
        received: orderItems?.filter(item => item.user_status === 'received').length || 0
      };
      setOrderStats(stats);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    }
  };

  const fetchTransactions = async (vendorId: string) => {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('transaction_created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setTransactions(data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    }
  };

  const fetchProductsFromSupabase = async (vendorId: string) => {
    try {
      const { data: productsData, error } = await supabase
        .from('products')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      const transformedProducts: Product[] = (productsData || []).map(product => ({
        ...product,
        original_price: product.original_price || product.price,
        detailed_description: product.detailed_description || product.description,
        subcategory: product.subcategory || '',
        condition: product.condition || 'new',
        updated_at: product.updated_at || product.created_at || new Date().toISOString(),
        tags: product.tags || [],
        specifications: product.specifications || {},
        shipping_info: product.shipping_info || {},
        warranty_info: product.warranty_info,
        delivery_campus_ids: product.delivery_campus_ids || []
      }));

      setProducts(transformedProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
  };

  const fetchReviews = async (vendorId: string) => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setReviews([]);
    }
  };

  const handleAcceptOrder = async (orderItemId: string) => {
    if (!vendorProfile) return;
    
    setProcessingOrder(orderItemId);
    try {
      const { error: updateError } = await supabase
        .from('order_items')
        .update({ 
          vendor_status: 'accepted',
          updated_at: new Date().toISOString()
        })
        .eq('id', orderItemId);

      if (updateError) throw updateError;

      await fetchOrders(vendorProfile.id);
      
      const orderItem = orders.find(item => item.id === orderItemId);
      if (orderItem) {
        const { data: orderData, error: orderError } = await supabase
          .from('orders')
          .select('user_id')
          .eq('id', orderItem.order_id)
          .single();

        if (orderError) {
          console.error('Error fetching order:', orderError);
        } else if (orderData?.user_id) {
          const { error: transactionError } = await supabase
            .from('transactions')
            .insert({
              order_id: orderItem.order_id,
              order_number: orderItem.order_id,
              product_id: orderItem.product_id,
              user_id: orderData.user_id,
              user_name: 'Customer',
              vendor_id: vendorProfile.id,
              vendor_name: vendorProfile.shopName,
              transaction_type: 'order_accepted',
              amount: orderItem.total_price,
              description: `Order accepted for ${orderItem.product_title}`,
              user_status_at_time: orderItem.user_status,
              vendor_status_at_time: 'accepted',
              order_created_at: orderItem.created_at,
              transaction_created_at: new Date().toISOString()
            });

          if (transactionError) console.error('Transaction record error:', transactionError);

          const productNames = extractProductNames([orderItem]);
          sendOrderStatusNotification(
            orderData.user_id,
            vendorProfile.shopName,
            orderItem.order_id,
            productNames,
            'accepted'
          );
        }
      }
    } catch (error) {
      console.error('Error accepting order:', error);
      alert('Failed to accept order. Please try again.');
    } finally {
      setProcessingOrder(null);
    }
  };

  const handleRejectOrder = async (orderItemId: string) => {
    if (!vendorProfile || !rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    setProcessingOrder(orderItemId);
    try {
      const { error: updateError } = await supabase
        .from('order_items')
        .update({ 
          vendor_status: 'rejected',
          rejection_reason: rejectionReason,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderItemId);

      if (updateError) throw updateError;

      const orderItem = orders.find(item => item.id === orderItemId);
      if (orderItem) {
        const { error: vendorBalanceError } = await supabase.rpc('update_vendor_balance', {
          vendor_id: vendorProfile.id,
          amount_change: -orderItem.total_price,
          balance_type: 'pending'
        });

        if (vendorBalanceError) console.error('Vendor balance update error:', vendorBalanceError);

        const { data: orderData, error: orderError } = await supabase
          .from('orders')
          .select('user_id')
          .eq('id', orderItem.order_id)
          .single();

        if (orderError) {
          console.error('Error fetching order:', orderError);
        } else if (orderData?.user_id) {
          const { error: transactionError } = await supabase
            .from('transactions')
            .insert({
              order_id: orderItem.order_id,
              order_number: orderItem.order_id,
              product_id: orderItem.product_id,
              user_id: orderData.user_id,
              user_name: 'Customer',
              vendor_id: vendorProfile.id,
              vendor_name: vendorProfile.shopName,
              transaction_type: 'order_rejected',
              amount: orderItem.total_price,
              description: `Order rejected: ${rejectionReason}`,
              user_status_at_time: orderItem.user_status,
              vendor_status_at_time: 'rejected',
              rejection_reason: rejectionReason,
              order_created_at: orderItem.created_at,
              transaction_created_at: new Date().toISOString()
            });

          if (transactionError) console.error('Transaction record error:', transactionError);

          const productNames = extractProductNames([orderItem]);
          sendOrderStatusNotification(
            orderData.user_id,
            vendorProfile.shopName,
            orderItem.order_id,
            productNames,
            'rejected'
          );
        }
      }

      await fetchOrders(vendorProfile.id);
      await fetchSupabaseStats(vendorProfile.id);
      
      setRejectionReason('');
    } catch (error) {
      console.error('Error rejecting order:', error);
      alert('Failed to reject order. Please try again.');
    } finally {
      setProcessingOrder(null);
    }
  };

  const handleMarkDelivered = async (orderItemId: string) => {
    if (!vendorProfile) return;
    
    setProcessingOrder(orderItemId);
    try {
      const orderItem = orders.find(item => item.id === orderItemId);
      if (!orderItem) throw new Error('Order not found');

      const { error: updateError } = await supabase
        .from('order_items')
        .update({ 
          updated_at: new Date().toISOString()
        })
        .eq('id', orderItemId);

      if (updateError) throw updateError;

      const { error: vendorBalanceError } = await supabase.rpc('update_vendor_balance', {
        vendor_id: vendorProfile.id,
        amount_change: -orderItem.total_price,
        balance_type: 'pending'
      });

      if (vendorBalanceError) throw vendorBalanceError;

      const { error: vendorAvailableError } = await supabase.rpc('update_vendor_balance', {
        vendor_id: vendorProfile.id,
        amount_change: orderItem.total_price,
        balance_type: 'available'
      });

      if (vendorAvailableError) throw vendorAvailableError;

      const { error: productError } = await supabase.rpc('increment_product_sales', {
        product_id: orderItem.product_id,
        increment_amount: orderItem.quantity
      });

      if (productError) throw productError;

      const { error: vendorStatsError } = await supabase.rpc('update_vendor_stats', {
        vendor_id_param: vendorProfile.id,
        sales_increment: orderItem.quantity,
        revenue_increment: orderItem.total_price
      });

      if (vendorStatsError) throw vendorStatsError;

      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('user_id')
        .eq('id', orderItem.order_id)
        .single();

      if (orderError) {
        console.error('Error fetching order:', orderError);
      } else if (orderData?.user_id) {
        const { error: transactionError } = await supabase
          .from('transactions')
          .insert({
            order_id: orderItem.order_id,
            order_number: orderItem.order_id,
            product_id: orderItem.product_id,
            user_id: orderData.user_id,
            user_name: 'Customer',
            vendor_id: vendorProfile.id,
            vendor_name: vendorProfile.shopName,
            transaction_type: 'delivery_completed',
            amount: orderItem.total_price,
            description: `Delivery completed for ${orderItem.product_title}`,
            user_status_at_time: 'received',
            vendor_status_at_time: 'delivered',
            order_created_at: orderItem.created_at,
            transaction_created_at: new Date().toISOString()
          });

        if (transactionError) console.error('Transaction record error:', transactionError);
      }

      await fetchOrders(vendorProfile.id);
      await fetchSupabaseStats(vendorProfile.id);
      await fetchProductsFromSupabase(vendorProfile.id);
      
      alert('Delivery marked as completed. Funds transferred to available balance.');
    } catch (error) {
      console.error('Error marking delivery:', error);
      alert('Failed to mark delivery. Please try again.');
    } finally {
      setProcessingOrder(null);
    }
  };

  const loadVendorProfile = async (vendorId: string) => {
    try {
      const vendorDoc = await getDoc(doc(db, 'vendors', vendorId));
      
      if (!vendorDoc.exists()) {
        navigate('/vendor-onboarding');
        return null;
      }

      const vendorData = vendorDoc.data();
      const profile: VendorProfile = {
        id: vendorDoc.id,
        userId: vendorData.userId,
        shopName: vendorData.shopName || '',
        profileImage: vendorData.profileImage || '',
        coverImage: vendorData.coverImage || '',
        bio: vendorData.bio || '',
        location: vendorData.location || '',
        contactPhone: vendorData.contactPhone || '',
        businessEmail: vendorData.businessEmail || '',
        website: vendorData.website || '',
        instagramHandle: vendorData.instagramHandle || '',
        facebookPage: vendorData.facebookPage || '',
        twitterHandle: vendorData.twitterHandle || '',
        businessHours: vendorData.businessHours || '',
        returnPolicy: vendorData.returnPolicy || '',
        shippingPolicy: vendorData.shippingPolicy || '',
        isActive: vendorData.isActive || false,
        onboardingCompleted: vendorData.onboardingCompleted || false,
        createdAt: vendorData.createdAt?.toDate().toISOString() || new Date().toISOString(),
        updatedAt: vendorData.updatedAt?.toDate().toISOString() || new Date().toISOString()
      };

      setVendorProfile(profile);
      return profile;
    } catch (error) {
      console.error('Error loading vendor profile:', error);
      navigate('/vendor-onboarding');
      return null;
    }
  };

  const loadVendorBusinesses = async (userId: string): Promise<VendorBusiness[]> => {
    try {
      const vendorsQuery = query(
        collection(db, 'vendors'),
        where('userId', '==', userId),
        where('isActive', '==', true)
      );
      
      const vendorsSnapshot = await getDocs(vendorsQuery);
      const businesses: VendorBusiness[] = vendorsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          shopName: data.shopName || '',
          profileImage: data.profileImage || '',
          isActive: data.isActive || false
        };
      });

      setVendorBusinesses(businesses);
      return businesses;
    } catch (error) {
      console.error('Error loading vendor businesses:', error);
      setVendorBusinesses([]);
      return [];
    }
  };

  const loadInitialData = async (userId: string, preferredVendorId?: string | null) => {
    try {
      setLoading(true);
      
      const businesses = await loadVendorBusinesses(userId);
      
      if (businesses.length > 0) {
        let targetVendorId = preferredVendorId;
        
        if (!targetVendorId || !businesses.some(b => b.id === targetVendorId)) {
          targetVendorId = businesses[0].id;
        }
        
        localStorage.setItem('lastSelectedShopId', targetVendorId);
        setSelectedShopId(targetVendorId);
        
        await loadVendorProfile(targetVendorId);
        await fetchOrders(targetVendorId);
        await fetchTransactions(targetVendorId);
        await fetchProductsFromSupabase(targetVendorId);
        await fetchSupabaseStats(targetVendorId);
        await fetchReviews(targetVendorId);
      } else {
        navigate('/vendor-onboarding');
        return;
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchUserProfile(user.uid);
        await loadInitialData(user.uid, selectedShopId);
      } else {
        navigate('/signin');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (vendorProfile && activeTab === 'products') {
      fetchProductsFromSupabase(vendorProfile.id);
    }
    if (vendorProfile && activeTab === 'overview') {
      fetchReviews(vendorProfile.id);
    }
  }, [activeTab, vendorProfile]);

  const handleShopSwitch = async (vendorId: string) => {
    if (!vendorId || vendorId === vendorProfile?.id) {
      setShowShopPopup(false);
      return;
    }

    setLoading(true);
    setShowShopPopup(false);
    
    try {
      localStorage.setItem('lastSelectedShopId', vendorId);
      setSelectedShopId(vendorId);
      
      await loadVendorProfile(vendorId);
      await fetchOrders(vendorId);
      await fetchTransactions(vendorId);
      await fetchProductsFromSupabase(vendorId);
      await fetchSupabaseStats(vendorId);
      await fetchReviews(vendorId);
    } catch (error) {
      console.error('Error switching shop:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateToShopSettings = () => {
    navigate('/vendor/settings');
  };

  const navigateToBalanceDetails = () => {
    navigate('/user/dashboard');
  };

  if (loading && !vendorProfile) {
    return (
      <div className="vendash-loading-state">
        <div className="vendash-spinner-loader"></div>
        <div>Loading your dashboard...</div>
      </div>
    );
  }

  if (!vendorProfile) {
    return (
      <div className="vendash-loading-state">
        <Building size={32} />
        <h3>No online Shop Found</h3>
        <p>Create your online shop to get started</p>
        <button 
          className="vendash-primary-action-button"
          onClick={() => navigate('/vendor-onboarding')}
        >
          <Plus size={12} /> Create Your online Shop
        </button>
      </div>
    );
  }

  // Cover image thumbnail
  const coverImageThumb = vendorProfile.coverImage
    ? getThumbnailUrl(vendorProfile.coverImage, 800, 60)
    : '';

  // Shop logo thumbnail
  const shopLogoThumb = vendorProfile.profileImage
    ? getThumbnailUrl(vendorProfile.profileImage, 120, 70)
    : '';

  // User avatar thumbnail
  const userAvatarThumb = userProfile?.profileImage
    ? getThumbnailUrl(userProfile.profileImage, 80, 70)
    : '';

  return (
    <div className="vendash-container">
      <header className="vendash-header-main">
        <div className="vendash-user-section">
          {userAvatarThumb ? (
            <img 
              src={userAvatarThumb} 
              alt="Profile" 
              className="vendash-user-avatar"
              referrerPolicy="no-referrer"
              onError={(e) => {
                const img = e.currentTarget;
                if (img.dataset.fallback === 'true') return;
                img.dataset.fallback = 'true';
                img.src = getOriginalUrl(userProfile?.profileImage || '');
              }}
            />
          ) : (
            <div className="vendash-user-avatar">
              <UserIcon size={12} />
            </div>
          )}
          <span className="vendash-username-text">
            {userProfile?.name || currentUser?.email?.split('@')[0] || 'Vendor'}
          </span>
        </div>
        
        <h1 className="vendash-header-title">Studio</h1>
        
        <div className="vendash-header-actions">
          {/* === My Ads button next to notification icon === */}
          <MyAdsButton variant="icon-label" label="My Ads" />

          <button 
            className="vendash-header-btn vendash-visit-store-btn"
            onClick={handleVisitAndCopyShop}
            title="Visit my shop & copy link"
          >
            <span className="vendash-visit-store-text">Shop</span>
            <ExternalLink size={16} />
          </button>
          <button 
            className="vendash-header-btn"
            onClick={() => navigate('/notifications')}
          >
            <Bell size={16} />
            {orderStats.pending > 0 && (
              <span className="vendash-notification-badge">{orderStats.pending}</span>
            )}
          </button>
          
          <button 
            className="vendash-header-btn"
            onClick={navigateToShopSettings}
          >
            <Settings size={16} />
          </button>
          
          <button 
            className="vendash-header-btn"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={16} />
          </button>
        </div>
      </header>

      <main className="vendash-main-content">
        <section className="vendash-shop-background-section">
          {coverImageThumb && (
            <img 
              src={coverImageThumb} 
              alt="Shop background" 
              className="vendash-shop-cover-image"
              referrerPolicy="no-referrer"
              onError={(e) => {
                const img = e.currentTarget;
                if (img.dataset.fallback === 'true') return;
                img.dataset.fallback = 'true';
                img.src = getOriginalUrl(vendorProfile.coverImage || '');
              }}
            />
          )}
          <div className="vendash-shop-overlay-content">
            <section className="vendash-shop-identity-card">
              <div className="vendash-shop-header-row">
                <div className="vendash-shop-logo-wrapper">
                  {shopLogoThumb ? (
                    <img 
                      src={shopLogoThumb} 
                      alt={vendorProfile.shopName}
                      className="vendash-shop-logo-image"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        const img = e.currentTarget;
                        if (img.dataset.fallback === 'true') return;
                        img.dataset.fallback = 'true';
                        img.src = getOriginalUrl(vendorProfile.profileImage || '');
                      }}
                    />
                  ) : (
                    <div className="vendash-shop-logo-fallback">
                      {vendorProfile.shopName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  {vendorBusinesses.length > 1 && (
                    <button 
                      className="vendash-switch-shop-button"
                      onClick={() => setShowShopPopup(true)}
                    >
                      <Store size={12} />
                    </button>
                  )}
                </div>
                
                <div className="vendash-shop-info-details">
                  <h2 className="vendash-shop-name-text">{vendorProfile.shopName}</h2>
                  <p className="vendash-shop-bio-text">{vendorProfile.bio || 'No bio yet'}</p>
                  <div className="vendash-shop-location-row">
                    <MapPin size={10} />
                    <span>{vendorProfile.location || 'No location set'}</span>
                  </div>

                  {/* === Boost button for the whole shop === */}
                  <div
                    className="vendash-shop-boost-wrapper"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <AdsPromoteButton
                      target={{
                        type: 'vendor',
                        id: vendorProfile.id,
                        name: vendorProfile.shopName || 'My Shop',
                        image: vendorProfile.profileImage || undefined,
                        category: 'vendor',
                        extra: {
                          vendor_id: vendorProfile.id,
                          shop_name: vendorProfile.shopName,
                          location: vendorProfile.location,
                          contact_phone: vendorProfile.contactPhone,
                          business_email: vendorProfile.businessEmail
                        }
                      }}
                      label="Boost Shop"
                      variant="icon-label"
                      className="vendash-shop-boost-btn"
                    />
                  </div>

                  <div
  className="vendash-shop-verify-wrapper"
  onClick={(e) => e.stopPropagation()}
>
  <VerifiedBadge
    shopId={vendorProfile.id}
    variant="button"
    className="vendash-shop-verify-btn"
  />
</div>
                </div>
              </div>
              
              <div className="vendash-shop-stats-grid">
                <div className="vendash-shop-stat-item">
                  <span className="vendash-shop-stat-label-text">
                    <Users size={10} /> Followers
                  </span>
                  <span className="vendash-shop-stat-value-number">
                    {supabaseStats?.followers_count || 0}
                  </span>
                </div>
                <div className="vendash-shop-stat-item">
                  <span className="vendash-shop-stat-label-text">
                    <Heart size={10} /> Likes
                  </span>
                  <span className="vendash-shop-stat-value-number">
                    {supabaseStats?.likes_count || 0}
                  </span>
                </div>
                <div className="vendash-shop-stat-item">
                  <span className="vendash-shop-stat-label-text">
                    <ShoppingCart size={10} /> Sales
                  </span>
                  <span className="vendash-shop-stat-value-number">
                    {supabaseStats?.sales_count || 0}
                  </span>
                </div>
                <div className="vendash-shop-stat-item">
                  <span className="vendash-shop-stat-label-text">
                    <DollarSign size={10} /> Revenue
                  </span>
                  <span className="vendash-shop-stat-value-number">
                    {formatPrice(supabaseStats?.total_revenue || 0)}
                  </span>
                </div>
              </div>
            </section>
          </div>
        </section>

        <section className="vendash-balance-overview-section">
          <h3 className="vendash-section-title-heading">
            <DollarSign size={12} /> Balance Overview
          </h3>
          <div className="vendash-balance-cards-grid">
            <div className="vendash-balance-card-item vendash-balance-card-pending">
              <div className="vendash-balance-icon-container">
                <Clock size={16} />
              </div>
              <div className="vendash-balance-info-content">
                <h4>Pending</h4>
                <p className={`vendash-balance-amount-value ${supabaseStats ? '' : 'loading'}`}>
                  {supabaseStats ? formatPrice(supabaseStats.pending_balance) : '-.-'}
                </p>
                <small>Awaiting confirmation</small>
              </div>
            </div>
            
            <div className="vendash-balance-card-item vendash-balance-card-available">
              <div className="vendash-balance-icon-container">
                <Wallet size={16} />
              </div>
              <div className="vendash-balance-info-content">
                <h4>Available</h4>
                <p className={`vendash-balance-amount-value ${supabaseStats ? '' : 'loading'}`}>
                  {supabaseStats ? formatPrice(supabaseStats.available_balance) : '-.-'}
                </p>
                <small>Ready to withdraw</small>
              </div>
            </div>

            <div className="vendash-balance-card-item vendash-balance-card-revenue">
              <div className="vendash-balance-icon-container">
                <Banknote size={16} />
              </div>
              <div className="vendash-balance-info-content">
                <h4>Total Revenue</h4>
                <p className="vendash-balance-amount-value">
                  {formatPrice(supabaseStats?.total_revenue || 0)}
                </p>
                <small>All-time earnings</small>
              </div>
            </div>

            <div className="vendash-balance-card-item vendash-balance-card-account">
              <div className="vendash-balance-icon-container">
                <CreditCard size={16} />
              </div>
              <div className="vendash-balance-info-content">
                <h4>Account</h4>
                <p className={`vendash-account-number-text ${supabaseStats ? '' : 'loading'}`}>
                  {supabaseStats?.virtual_account || 'VND---'}
                </p>
                <small>Business account</small>
              </div>
            </div>
          </div>
        </section>

        <nav className="vendash-tabs-navigation">
          <button 
            className={`vendash-tab-button ${activeTab === 'orders' ? 'vendash-tab-button-active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            <ShoppingCart size={14} />
            <span>Orders</span>
            {orderStats.pending > 0 && (
              <span className="vendash-tab-notification">{orderStats.pending}</span>
            )}
          </button>
          <button 
            className={`vendash-tab-button ${activeTab === 'transactions' ? 'vendash-tab-button-active' : ''}`}
            onClick={() => setActiveTab('transactions')}
          >
            <FileText size={14} />
            <span>Transactions</span>
          </button>
          <button 
            className={`vendash-tab-button ${activeTab === 'products' ? 'vendash-tab-button-active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            <Package size={14} />
            <span>Products</span>
          </button>
          <button 
            className={`vendash-tab-button ${activeTab === 'overview' ? 'vendash-tab-button-active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <Grid size={14} />
            <span>Overview</span>
          </button>
          <button 
            className={`vendash-tab-button ${activeTab === 'analytics' ? 'vendash-tab-button-active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <BarChart3 size={14} />
            <span>Analytics</span>
          </button>
        </nav>

        <div className="vendash-tab-content-area">
          {activeTab === 'orders' && (
            <OrdersTab 
              orders={orders}
              orderStats={orderStats}
              onAcceptOrder={handleAcceptOrder}
              onRejectOrder={handleRejectOrder}
              onMarkDelivered={handleMarkDelivered}
              processingOrder={processingOrder}
              rejectionReason={rejectionReason}
              onRejectionReasonChange={setRejectionReason}
              onViewOrderInfo={handleViewOrderInfo}
            />
          )}

          {activeTab === 'transactions' && (
            <TransactionsTab 
              orders={orders}
              transactions={transactions}
            />
          )}

          {activeTab === 'products' && (
            <ProductsTab 
              products={products}
              onAddProduct={() => navigate('/vendor/add-product')}
              onEditProduct={(productId) => navigate(`/vendor/edit-product/${productId}`)}
              onDeleteProduct={async (productId) => {
                if (window.confirm('Are you sure you want to delete this product?')) {
                  try {
                    const { error } = await supabase
                      .from('products')
                      .delete()
                      .eq('id', productId);

                    if (error) throw error;
                    
                    if (vendorProfile) {
                      fetchProductsFromSupabase(vendorProfile.id);
                    }
                  } catch (error) {
                    console.error('Error deleting product:', error);
                    alert('Failed to delete product');
                  }
                }
              }}
              onNavigateToShopSettings={navigateToShopSettings}
            />
          )}

          {activeTab === 'overview' && (
            <OverviewTab 
              vendorProfile={vendorProfile}
              products={products}
              orderStats={orderStats}
              supabaseStats={supabaseStats}
              reviews={reviews}
              onAddProduct={() => navigate('/vendor/add-product')}
              onViewProducts={() => setActiveTab('products')}
              onViewOrders={() => setActiveTab('orders')}
              onNavigateToShopSettings={navigateToShopSettings}
              onNavigateToBalanceDetails={navigateToBalanceDetails}
            />
          )}

          {activeTab === 'analytics' && <AnalyticsTab />}
        </div>
      </main>

      {showShopPopup && (
        <div className="vendash-popup-overlay-bg" onClick={() => setShowShopPopup(false)}>
          <div className="vendash-shop-popup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vendash-popup-header-section">
              <h3>Switch Shop</h3>
              <button 
                className="vendash-close-popup-button"
                onClick={() => setShowShopPopup(false)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="vendash-shops-list-container">
              {vendorBusinesses.map(shop => {
                const shopThumb = shop.profileImage
                  ? getThumbnailUrl(shop.profileImage, 80, 70)
                  : '';
                return (
                  <div 
                    key={shop.id} 
                    className={`vendash-shop-list-item ${shop.id === vendorProfile.id ? 'vendash-shop-list-item-active' : ''}`}
                    onClick={() => handleShopSwitch(shop.id)}
                  >
                    <div className="vendash-shop-logo-small-image">
                      {shopThumb ? (
                        <img 
                          src={shopThumb} 
                          alt={shop.shopName}
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            const img = e.currentTarget;
                            if (img.dataset.fallback === 'true') return;
                            img.dataset.fallback = 'true';
                            img.src = getOriginalUrl(shop.profileImage);
                          }}
                        />
                      ) : (
                        <div className="vendash-shop-logo-small-fallback">
                          {shop.shopName.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="vendash-shop-details-content">
                      <h4>{shop.shopName}</h4>
                      <span className="vendash-shop-status-text">
                        {shop.isActive ? 'Active' : 'Inactive'}
                        {shop.id === vendorProfile.id && ' • Current'}
                      </span>
                    </div>
                    <div className="vendash-shop-arrow-icon">
                      <ChevronRight size={16} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="vendash-popup-footer-section">
              <button 
                className="vendash-create-shop-action-button"
                onClick={() => {
                  setShowShopPopup(false);
                  navigate('/vendor-onboarding');
                }}
              >
                <Plus size={14} /> Create New Shop
              </button>
            </div>
          </div>
        </div>
      )}

      {showOrderInfoModal.show && showOrderInfoModal.orderItem && (
        <div className="vendash-info-modal-overlay" onClick={() => setShowOrderInfoModal({show: false})}>
          <div className="vendash-info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vendash-info-header">
              <h3>
                <Info size={12} />
                Order Details
              </h3>
              <button className="vendash-info-close" onClick={() => setShowOrderInfoModal({show: false})}>
                <X size={14} />
              </button>
            </div>
            <div className="vendash-info-body">
              <div className="vendash-info-section">
                <div className="vendash-info-section-title">
                  <Package size={10} />
                  Product
                </div>
                <div className="vendash-info-row">
                  <span>Product ID:</span>
                  <span className="code">{showOrderInfoModal.orderItem.product_id}</span>
                </div>
                <div className="vendash-info-row">
                  <span>Title:</span>
                  <span><strong>{showOrderInfoModal.orderItem.product_title}</strong></span>
                </div>
                <div className="vendash-info-row">
                  <span>Quantity:</span>
                  <span>{showOrderInfoModal.orderItem.quantity}</span>
                </div>
                <div className="vendash-info-row">
                  <span>Unit Price:</span>
                  <span>{formatPrice(showOrderInfoModal.orderItem.unit_price)}</span>
                </div>
                <div className="vendash-info-row">
                  <span>Total Price:</span>
                  <span><strong>{formatPrice(showOrderInfoModal.orderItem.total_price)}</strong></span>
                </div>
              </div>

              <div className="vendash-info-section">
                <div className="vendash-info-section-title">
                  <UserIcon size={10} />
                  Customer
                </div>
                <div className="vendash-info-row">
                  <span>Order ID:</span>
                  <span className="code">{showOrderInfoModal.orderItem.order_id}</span>
                </div>
                <div className="vendash-info-row">
                  <span>Buyer Status:</span>
                  <span className={`status-${showOrderInfoModal.orderItem.user_status}`}>
                    {showOrderInfoModal.orderItem.user_status}
                  </span>
                </div>
                <div className="vendash-info-row">
                  <span>Your Status:</span>
                  <span className={`status-${showOrderInfoModal.orderItem.vendor_status}`}>
                    {showOrderInfoModal.orderItem.vendor_status}
                  </span>
                </div>
                <div className="vendash-info-row">
                  <span>Contact:</span>
                  <span className="contact-phone">
                    {showOrderInfoModal.buyerPhone || 'Loading...'}
                  </span>
                </div>
                {showOrderInfoModal.orderItem.rejection_reason && (
                  <div className="vendash-info-row">
                    <span>Rejection Reason:</span>
                    <span style={{color: '#dc3545'}}>{showOrderInfoModal.orderItem.rejection_reason}</span>
                  </div>
                )}
              </div>

              <div className="vendash-info-section">
                <div className="vendash-info-section-title">
                  <Clock size={10} />
                  Timeline
                </div>
                <div className="vendash-info-row">
                  <span>Order Created:</span>
                  <span className="vendash-info-timestamp">
                    {new Date(showOrderInfoModal.orderItem.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="vendash-info-row">
                  <span>Last Updated:</span>
                  <span className="vendash-info-timestamp">
                    {new Date(showOrderInfoModal.orderItem.updated_at).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="vendash-info-section">
                <div className="vendash-info-section-title">
                  <MapPin size={10} />
                  Delivery Location
                </div>
                <div className="vendash-info-location">
                  {showOrderInfoModal.formattedLocation ? (
                    <>
                      {showOrderInfoModal.formattedLocation.full_location && (
                        <><p><strong>📍 Full Address:</strong></p><p>{showOrderInfoModal.formattedLocation.full_location}</p></>
                      )}
                      {showOrderInfoModal.formattedLocation.state_name && !showOrderInfoModal.formattedLocation.state_name.includes('ID:') && (
                        <p><strong>State:</strong> {showOrderInfoModal.formattedLocation.state_name}</p>
                      )}
                      {showOrderInfoModal.formattedLocation.university_name && !showOrderInfoModal.formattedLocation.university_name.includes('ID:') && (
                        <p><strong>University:</strong> {showOrderInfoModal.formattedLocation.university_name}</p>
                      )}
                      {showOrderInfoModal.formattedLocation.campus_name && !showOrderInfoModal.formattedLocation.campus_name.includes('ID:') && (
                        <p><strong>Campus:</strong> {showOrderInfoModal.formattedLocation.campus_name}</p>
                      )}
                      {showOrderInfoModal.formattedLocation.precise_location && (
                        <p><strong>Precise Location:</strong> {showOrderInfoModal.formattedLocation.precise_location}</p>
                      )}
                    </>
                  ) : (
                    <p>No delivery location information available</p>
                  )}
                </div>
              </div>

              <div className="vendash-info-section">
                <div className="vendash-info-section-title">
                  <ShoppingCart size={10} />
                  Order Reference
                </div>
                <div className="vendash-info-row">
                  <span>Order Item ID:</span>
                  <span className="code">{showOrderInfoModal.orderItem.id}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Tab Components
const OrdersTab: React.FC<{
  orders: OrderItem[];
  orderStats: OrderStats;
  onAcceptOrder: (orderId: string) => Promise<void>;
  onRejectOrder: (orderId: string) => Promise<void>;
  onMarkDelivered: (orderId: string) => Promise<void>;
  processingOrder: string | null;
  rejectionReason: string;
  onRejectionReasonChange: (reason: string) => void;
  onViewOrderInfo: (orderItem: OrderItem) => void;
}> = ({ 
  orders, 
  orderStats, 
  onAcceptOrder, 
  onRejectOrder, 
  onMarkDelivered,
  processingOrder,
  rejectionReason,
  onRejectionReasonChange,
  onViewOrderInfo
}) => {
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  const getStatusColor = (vendorStatus: string, userStatus: string) => {
    if (userStatus === 'cancelled') return '#dc2626';
    if (vendorStatus === 'rejected') return '#ef4444';
    if (vendorStatus === 'accepted' && userStatus === 'received') return '#10b981';
    if (vendorStatus === 'accepted') return '#3b82f6';
    if (vendorStatus === 'pending') return '#f59e0b';
    return '#6b7280';
  };

  const getStatusText = (vendorStatus: string, userStatus: string) => {
    if (userStatus === 'cancelled') return 'Cancelled by Buyer';
    if (vendorStatus === 'rejected') return 'Rejected by You';
    if (vendorStatus === 'accepted' && userStatus === 'received') return 'Received Successfully!';
    if (vendorStatus === 'accepted') return 'Accepted';
    if (vendorStatus === 'pending') return 'Pending Review';
    return 'Unknown';
  };

  const handleFilterClick = (filter: string) => {
    if (filter === 'total') {
      setSelectedFilters([]);
    } else {
      setSelectedFilters(prev => {
        if (prev.includes(filter)) {
          return prev.filter(f => f !== filter);
        } else {
          return [...prev, filter];
        }
      });
    }
  };

  const filteredOrders = selectedFilters.length > 0 
    ? orders.filter(order => {
        if (selectedFilters.includes('pending') && order.vendor_status === 'pending' && order.user_status !== 'cancelled') return true;
        if (selectedFilters.includes('accepted') && order.vendor_status === 'accepted') return true;
        if (selectedFilters.includes('rejected') && order.vendor_status === 'rejected') return true;
        if (selectedFilters.includes('received') && order.user_status === 'received') return true;
        return false;
      })
    : orders;

  return (
    <div className="vendash-orders-tab">
      <div className="vendash-order-stats-grid">
        <div 
          className={`vendash-order-stat-card ${selectedFilters.length === 0 ? 'vendash-order-stat-card-active' : ''}`}
          onClick={() => handleFilterClick('total')}
        >
          <div className="vendash-order-stat-content-inline">
            <ClipboardCheck size={12} />
            <span>Total</span>
            <p className="vendash-order-stat-value">{orderStats.total}</p>
          </div>
          {selectedFilters.length === 0 && <div className="vendash-stat-active-indicator" />}
        </div>
        <div 
          className={`vendash-order-stat-card ${selectedFilters.includes('pending') ? 'vendash-order-stat-card-active' : ''}`}
          onClick={() => handleFilterClick('pending')}
        >
          <div className="vendash-order-stat-content-inline">
            <AlertTriangle size={12} />
            <span>Pending</span>
            <p className="vendash-order-stat-value">{orderStats.pending}</p>
          </div>
          {selectedFilters.includes('pending') && <div className="vendash-stat-active-indicator" />}
        </div>
        <div 
          className={`vendash-order-stat-card ${selectedFilters.includes('accepted') ? 'vendash-order-stat-card-active' : ''}`}
          onClick={() => handleFilterClick('accepted')}
        >
          <div className="vendash-order-stat-content-inline">
            <Check size={12} />
            <span>Accepted</span>
            <p className="vendash-order-stat-value">{orderStats.accepted}</p>
          </div>
          {selectedFilters.includes('accepted') && <div className="vendash-stat-active-indicator" />}
        </div>
        <div 
          className={`vendash-order-stat-card ${selectedFilters.includes('received') ? 'vendash-order-stat-card-active' : ''}`}
          onClick={() => handleFilterClick('received')}
        >
          <div className="vendash-order-stat-content-inline">
            <CheckCircle size={12} />
            <span>Received</span>
            <p className="vendash-order-stat-value">{orderStats.received}</p>
          </div>
          {selectedFilters.includes('received') && <div className="vendash-stat-active-indicator" />}
        </div>
      </div>

      <div className="vendash-orders-list">
        {filteredOrders.length === 0 ? (
          <div className="vendash-empty-state-container">
            <div className="vendash-empty-icon-container">
              <ShoppingCart size={24} />
            </div>
            <h4>No Orders Found</h4>
            <p>{selectedFilters.length > 0 ? 'Try changing your filters' : 'When customers purchase your products, orders will appear here'}</p>
          </div>
        ) : (
          filteredOrders.map(order => (
            <div key={order.id} className="vendash-order-item">
              <div 
                className="vendash-order-header"
                onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
              >
                <div className="vendash-order-title-section">
                  <h4 className="vendash-order-product-title">
                    {order.product_title} <span className="vendash-order-product-id">(ID: {order.order_id})</span>
                  </h4>
                  <div 
                    className="vendash-order-status-badge"
                    style={{ backgroundColor: getStatusColor(order.vendor_status, order.user_status) }}
                  >
                    {getStatusText(order.vendor_status, order.user_status)}
                  </div>
                  <button
                    className="vendash-order-info-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewOrderInfo(order);
                    }}
                    title="View full order details"
                  >
                    <Info size={12} />
                  </button>
                </div>
                <div className="vendash-order-price-section">
                  <p className="vendash-order-price">{formatPrice(order.total_price)}</p>
                  <span className="vendash-order-quantity">x{order.quantity}</span>
                  {expandedOrder === order.id ? (
                    <ChevronUp size={12} className="vendash-order-expand-icon" />
                  ) : (
                    <ChevronDown size={12} className="vendash-order-expand-icon" />
                  )}
                </div>
              </div>

              {expandedOrder === order.id && (
                <div className="vendash-order-details">
                  <div className="vendash-order-detail-row vendash-order-detail-row-alt">
                    <span className="vendash-order-detail-label">Order ID:</span>
                    <span className="vendash-order-detail-value">{order.order_id}</span>
                  </div>
                  <div className="vendash-order-detail-row">
                    <span className="vendash-order-detail-label">Product ID:</span>
                    <span className="vendash-order-detail-value">{order.product_id}</span>
                  </div>
                  <div className="vendash-order-detail-row vendash-order-detail-row-alt">
                    <span className="vendash-order-detail-label">Unit Price:</span>
                    <span className="vendash-order-detail-value">{formatPrice(order.unit_price)}</span>
                  </div>
                  <div className="vendash-order-detail-row">
                    <span className="vendash-order-detail-label">Quantity:</span>
                    <span className="vendash-order-detail-value">{order.quantity}</span>
                  </div>
                  <div className="vendash-order-detail-row vendash-order-detail-row-alt">
                    <span className="vendash-order-detail-label">Total Price:</span>
                    <span className="vendash-order-detail-value">{formatPrice(order.total_price)}</span>
                  </div>
                  <div className="vendash-order-detail-row">
                    <span className="vendash-order-detail-label">Order Date:</span>
                    <span className="vendash-order-detail-value">{formatDate(order.created_at)}</span>
                  </div>
                  <div className="vendash-order-detail-row vendash-order-detail-row-alt">
                    <span className="vendash-order-detail-label">Buyer Status:</span>
                    <span className="vendash-order-detail-value">{order.user_status}</span>
                  </div>
                  <div className="vendash-order-detail-row">
                    <span className="vendash-order-detail-label">Your Status:</span>
                    <span className="vendash-order-detail-value">{order.vendor_status}</span>
                  </div>
                  
                  {order.location_details && (
                    <div className="vendash-order-detail-row vendash-order-detail-row-alt">
                      <span className="vendash-order-detail-label">Delivery Location:</span>
                      <span className="vendash-order-detail-value">
                        {order.location_details.full_location || order.location_details.delivery_location || 'N/A'}
                      </span>
                    </div>
                  )}

                  {order.rejection_reason && (
                    <div className="vendash-order-detail-row vendash-order-detail-row-alt">
                      <span className="vendash-order-detail-label">Rejection Reason:</span>
                      <span className="vendash-order-detail-value">{order.rejection_reason}</span>
                    </div>
                  )}

                  <div className="vendash-order-actions">
                    {order.user_status === 'cancelled' ? (
                      <div className="vendash-order-cancelled-note">
                        Order was cancelled by the buyer
                      </div>
                    ) : order.vendor_status === 'pending' ? (
                      <>
                        <button
                          className="vendash-order-action-btn vendash-accept-btn"
                          onClick={() => onAcceptOrder(order.id)}
                          disabled={processingOrder === order.id}
                        >
                          {processingOrder === order.id ? (
                            <RefreshCw size={12} className="vendash-spinning-icon" />
                          ) : (
                            <Check size={12} />
                          )}
                          <span>Accept Order</span>
                        </button>
                        <button
                          className="vendash-order-action-btn vendash-reject-btn"
                          onClick={() => {
                            if (!rejectionReason.trim() && window.confirm('Reject without reason?')) {
                              onRejectionReasonChange('No reason provided');
                              setTimeout(() => onRejectOrder(order.id), 100);
                            } else {
                              onRejectOrder(order.id);
                            }
                          }}
                          disabled={processingOrder === order.id}
                        >
                          {processingOrder === order.id ? (
                            <RefreshCw size={12} className="vendash-spinning-icon" />
                          ) : (
                            <X size={12} />
                          )}
                          <span>Reject Order</span>
                        </button>
                        <div className="vendash-rejection-reason-input">
                          <input
                            type="text"
                            placeholder="Reason for rejection (optional)"
                            value={rejectionReason}
                            onChange={(e) => onRejectionReasonChange(e.target.value)}
                            className="vendash-reason-input"
                          />
                        </div>
                      </>
                    ) : order.vendor_status === 'accepted' && order.user_status === 'received' ? (
                      <button
                        className="vendash-order-action-btn vendash-deliver-btn"
                        onClick={() => onMarkDelivered(order.id)}
                        disabled={processingOrder === order.id}
                      >
                        {processingOrder === order.id ? (
                          <RefreshCw size={12} className="vendash-spinning-icon" />
                        ) : (
                          <TruckIcon size={12} />
                        )}
                        <span>Received Successfully!</span>
                      </button>
                    ) : order.vendor_status === 'accepted' ? (
                      <div className="vendash-waiting-received">
                        Waiting for buyer to mark as received
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const TransactionsTab: React.FC<{
  orders: OrderItem[];
  transactions: Transaction[];
}> = ({ orders, transactions }) => {
  const receivedOrders = orders.filter(order => order.user_status === 'received');

  const receivedOrderTransactions = receivedOrders.map(order => ({
    id: `order-${order.id}`,
    order_id: order.order_id,
    order_number: order.order_id,
    amount: order.total_price,
    description: `Order received for ${order.product_title}`,
    user_status_at_time: 'received',
    vendor_status_at_time: order.vendor_status,
    transaction_created_at: order.updated_at || order.created_at,
    transaction_type: 'delivery_completed'
  }));

  const relevantTransactions = transactions.filter(t => 
    t.user_status_at_time === 'received' || 
    t.transaction_type === 'delivery_completed'
  );

  const allReceivedItems = [...receivedOrderTransactions, ...relevantTransactions]
    .sort((a, b) => new Date(b.transaction_created_at).getTime() - new Date(a.transaction_created_at).getTime());

  return (
    <div className="vendash-transactions-tab">
      <div className="vendash-section-header-row">
        <h3 className="vendash-section-title-heading">Transaction History</h3>
        <p>View all your successfully received orders</p>
      </div>

      {allReceivedItems.length === 0 ? (
        <div className="vendash-empty-state-container">
          <div className="vendash-empty-icon-container">
            <FileText size={24} />
          </div>
          <h4>No Transactions Yet</h4>
          <p>When orders are marked as received, they will appear here</p>
        </div>
      ) : (
        <div className="vendash-transactions-list">
          {allReceivedItems.map((item, index) => (
            <div key={item.id || index} className="vendash-transaction-item">
              <div className="vendash-transaction-header">
                <div className="vendash-transaction-type-section">
                  <div 
                    className="vendash-transaction-type-badge received"
                    style={{ backgroundColor: '#10b981' }}
                  >
                    Received Order
                  </div>
                  <span className="vendash-transaction-order">ID: {item.order_number}</span>
                </div>
                <div className="vendash-transaction-amount-section">
                  <p className="vendash-transaction-amount">{formatPrice(item.amount)}</p>
                  <span className="vendash-transaction-date">
                    {formatDate(item.transaction_created_at)}
                  </span>
                </div>
              </div>
              
              <div className="vendash-transaction-details">
                <div className="vendash-transaction-detail-row vendash-transaction-detail-row-alt">
                  <span className="vendash-transaction-detail-label">Description:</span>
                  <span className="vendash-transaction-detail-value">
                    {item.description || 'Order successfully received'}
                  </span>
                </div>
                <div className="vendash-transaction-detail-row">
                  <span className="vendash-transaction-detail-label">Status:</span>
                  <span className="vendash-transaction-detail-value">
                    <span className="received-highlight">✓ Received</span>
                  </span>
                </div>
                <div className="vendash-transaction-detail-row vendash-transaction-detail-row-alt">
                  <span className="vendash-transaction-detail-label">Date:</span>
                  <span className="vendash-transaction-detail-value">{formatDate(item.transaction_created_at)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProductsTab: React.FC<{
  products: Product[];
  onAddProduct: () => void;
  onEditProduct: (productId: string) => void;
  onDeleteProduct: (productId: string) => Promise<void>;
  onNavigateToShopSettings: () => void;
}> = ({ products, onAddProduct, onEditProduct, onDeleteProduct, onNavigateToShopSettings }) => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [productFilter, setProductFilter] = useState<'all' | 'active' | 'inactive' | 'promoted'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'price_low' | 'price_high' | 'popular'>('newest');
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);

  useEffect(() => {
    filterAndSortProducts();
  }, [products, productFilter, sortBy]);

  const filterAndSortProducts = () => {
    let filtered = [...products];

    if (productFilter !== 'all') {
      if (productFilter === 'active') {
        filtered = filtered.filter(product => product.is_active);
      } else if (productFilter === 'inactive') {
        filtered = filtered.filter(product => !product.is_active);
      } else if (productFilter === 'promoted') {
        filtered = filtered.filter(product => product.is_promoted);
      }
    }

    switch (sortBy) {
      case 'price_low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price_high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'popular':
        filtered.sort((a, b) => (b.views_count || 0) - (a.views_count || 0));
        break;
      case 'newest':
      default:
        filtered.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        break;
    }

    setFilteredProducts(filtered);
  };

  return (
    <div className="vendash-products-tab">
      <div className="vendash-tab-header-row">
        <div>
          <h3 className="vendash-section-title-heading">Product Management</h3>
          <p>Manage your products and services</p>
        </div>
        <button className="vendash-add-product-button" onClick={onAddProduct}>
          <Plus size={12} />
          <span>Add Product or Service</span>
        </button>
      </div>

      <div className="vendash-products-controls">
        <div className="vendash-filter-controls">
          <select 
            className="vendash-filter-select"
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value as any)}
          >
            <option value="all">All Products</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="promoted">Promoted</option>
          </select>

          <select 
            className="vendash-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
          >
            <option value="newest">Newest First</option>
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
            <option value="popular">Most Popular</option>
          </select>
        </div>
      </div>

      <div className="vendash-products-count">
        <span>{filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}</span>
        {productFilter !== 'all' && (
          <button 
            className="vendash-clear-filter-btn"
            onClick={() => setProductFilter('all')}
          >
            Clear Filter
          </button>
        )}
      </div>

      {filteredProducts.length === 0 ? (
        <div className="vendash-empty-state-container">
          <div className="vendash-empty-icon-container">
            <ShoppingBag size={24} />
          </div>
          <h4>No Products or services Found</h4>
          <p>{productFilter !== 'all' ? 'Try changing your filter' : 'Start by adding your first product or service'}</p>
          <button className="vendash-cta-primary-button" onClick={onAddProduct}>
            <Plus size={12} /> Post Product or Service
          </button>
        </div>
      ) : (
        <div className={`vendash-products-display ${viewMode === 'grid' ? 'vendash-products-grid-view' : 'vendash-products-list-view'}`}>
          {filteredProducts.map((product) => {
            const originalImg = product.images && product.images[0] ? product.images[0] : '';
            const thumbSrc = originalImg ? getThumbnailUrl(originalImg, 400, 65) : '';

            return (
              <div key={product.id} className="vendash-product-item-rectangular">
                <div className="vendash-product-image-container">
                  {thumbSrc ? (
                    <img 
                      src={thumbSrc} 
                      alt={product.title}
                      className="vendash-product-main-image"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        const img = e.currentTarget;
                        if (img.dataset.fallback === 'true') return;
                        img.dataset.fallback = 'true';
                        if (originalImg) img.src = getOriginalUrl(originalImg);
                      }}
                    />
                  ) : (
                    <div className="vendash-product-image-placeholder">
                      <Package size={24} />
                    </div>
                  )}
                  
                  <div className={`vendash-product-status-badge ${product.is_active ? 'vendash-product-active' : 'vendash-product-inactive'}`}>
                    {product.is_active ? 'Active' : 'Inactive'}
                  </div>
                  
                  {product.is_promoted && (
                    <div className="vendash-product-promoted-badge">
                      🔥 Promoted
                    </div>
                  )}

                  {/* === Boost button on each vendor product === */}
                  <div
                    className="vendash-product-boost-wrapper"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <AdsPromoteButton
                      target={{
                        type: 'product',
                        id: product.id,
                        name: product.title || 'Product',
                        image: originalImg || undefined,
                        category: product.category || undefined,
                        extra: {
                          price: product.price,
                          vendor_id: product.vendor_id,
                          vendor_name: product.vendor_name
                        }
                      }}
                      label="Boost"
                      variant="icon-label"
                      className="vendash-product-boost-btn"
                    />
                  </div>
                </div>
                
                <div className="vendash-product-info-section">
                  <div className="vendash-product-header">
                    <h4 className="vendash-product-name">
                      {product.title}
                    </h4>
                    <div className="vendash-product-price">
                      {formatPrice(product.price)}
                    </div>
                  </div>
                  
                  <div className="vendash-product-stats-row">
                    <div className="vendash-product-stat">
                      <Eye size={10} />
                      <span>{product.views_count}</span>
                    </div>
                    <div className="vendash-product-stat">
                      <Heart size={10} />
                      <span>{product.likes_count}</span>
                    </div>
                    <div className="vendash-product-stat">
                      <ShoppingCart size={10} />
                      <span>{product.sales_count}</span>
                    </div>
                  </div>
                  
                  <div className="vendash-product-inventory-info">
                    <span className="vendash-inventory-label">Stock:</span>
                    <span className="vendash-inventory-value">
                      {product.inventory} units
                    </span>
                  </div>
                  
                  <div className="vendash-product-revenue-info">
                    <span className="vendash-revenue-label">Revenue:</span>
                    <span className="vendash-revenue-value">
                      {formatPrice(product.price * product.sales_count)}
                    </span>
                  </div>
                  
                  <div className="vendash-product-actions">
                    <button 
                      className="vendash-product-action-btn vendash-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigateToShopSettings();
                      }}
                    >
                      <Edit size={10} />
                      <span>Edit</span>
                    </button>
                    
                    <button 
                      className="vendash-product-action-btn vendash-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteProduct(product.id);
                      }}
                    >
                      <Trash2 size={10} />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const OverviewTab: React.FC<{
  vendorProfile: VendorProfile;
  products: Product[];
  orderStats: OrderStats;
  supabaseStats: any;
  reviews: Review[];
  onAddProduct: () => void;
  onViewProducts: () => void;
  onViewOrders: () => void;
  onNavigateToShopSettings: () => void;
  onNavigateToBalanceDetails: () => void;
}> = ({ vendorProfile, products, orderStats, supabaseStats, reviews, onAddProduct, onViewProducts, onViewOrders, onNavigateToShopSettings, onNavigateToBalanceDetails }) => {
  
  const stats = {
    totalViews: products.reduce((sum, product) => sum + product.views_count, 0),
    totalLikes: products.reduce((sum, product) => sum + product.likes_count, 0),
    totalSales: products.reduce((sum, product) => sum + product.sales_count, 0),
    totalRevenue: products.reduce((sum, product) => sum + (product.price * product.sales_count), 0),
    totalProducts: products.length,
    activeProducts: products.filter(p => p.is_active).length,
    averageRating: reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0
  };

  return (
    <div className="vendash-overview-tab">
      <div className="vendash-stats-grid-overview">
        <div className="vendash-stat-card-item">
          <div className="vendash-stat-icon-wrapper">
            <Package size={14} />
          </div>
          <div className="vendash-stat-details-content">
            <h3>Total Products</h3>
            <p className="vendash-stat-number-value">{stats.totalProducts}</p>
          </div>
        </div>
        <div className="vendash-stat-card-item">
          <div className="vendash-stat-icon-wrapper">
            <Eye size={14} />
          </div>
          <div className="vendash-stat-details-content">
            <h3>Total Views</h3>
            <p className="vendash-stat-number-value">{stats.totalViews}</p>
          </div>
        </div>
        <div className="vendash-stat-card-item">
          <div className="vendash-stat-icon-wrapper">
            <Heart size={14} />
          </div>
          <div className="vendash-stat-details-content">
            <h3>Product Likes</h3>
            <p className="vendash-stat-number-value">{stats.totalLikes}</p>
          </div>
        </div>
        <div className="vendash-stat-card-item">
          <div className="vendash-stat-icon-wrapper">
            <ShoppingCart size={14} />
          </div>
          <div className="vendash-stat-details-content">
            <h3>Total Orders</h3>
            <p className="vendash-stat-number-value">{orderStats.total}</p>
          </div>
        </div>
        <div className="vendash-stat-card-item">
          <div className="vendash-stat-icon-wrapper">
            <CheckCircle size={14} />
          </div>
          <div className="vendash-stat-details-content">
            <h3>Accepted Orders</h3>
            <p className="vendash-stat-number-value">{orderStats.accepted}</p>
          </div>
        </div>
        <div className="vendash-stat-card-item">
          <div className="vendash-stat-icon-wrapper">
            <DollarSign size={14} />
          </div>
          <div className="vendash-stat-details-content">
            <h3>Total Revenue</h3>
            <p className="vendash-stat-number-value">{formatPrice(stats.totalRevenue)}</p>
          </div>
        </div>
      </div>

      <div className="vendash-quick-actions-section">
        <h3 className="vendash-section-title-heading">Quick Actions</h3>
        <div className="vendash-actions-grid-buttons">
          <button className="vendash-action-button-item" onClick={onAddProduct}>
            <Plus size={14} />
            <span>Add Product</span>
          </button>
          <button className="vendash-action-button-item" onClick={onViewOrders}>
            <ShoppingCart size={14} />
            <span>View Orders ({orderStats.pending} pending)</span>
          </button>
          <button className="vendash-action-button-item" onClick={onNavigateToShopSettings}>
            <Settings size={14} />
            <span>Shop Settings</span>
          </button>
          <button className="vendash-action-button-item" onClick={onNavigateToBalanceDetails}>
            <Wallet size={14} />
            <span>Balance Details</span>
          </button>
        </div>
      </div>

      {reviews.length > 0 && (
        <div className="vendash-reviews-section">
          <h3 className="vendash-section-title-heading">Recent Reviews</h3>
          <div className="vendash-reviews-summary">
            <div className="vendash-average-rating">
              <Star size={16} fill="#fbbf24" color="#fbbf24" />
              <span>{stats.averageRating.toFixed(1)}</span>
              <span className="vendash-total-reviews">({reviews.length} reviews)</span>
            </div>
          </div>
          <div className="vendash-reviews-list">
            {reviews.slice(0, 3).map(review => (
              <div key={review.id} className="vendash-review-item">
                <div className="vendash-review-header">
                  <span className="vendash-reviewer-name">{review.user_name}</span>
                  <div className="vendash-review-rating">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star 
                        key={i} 
                        size={12} 
                        fill={i < review.rating ? '#fbbf24' : 'none'} 
                        color={i < review.rating ? '#fbbf24' : '#d1d5db'}
                      />
                    ))}
                  </div>
                </div>
                <p className="vendash-review-text">{review.review_text}</p>
                <span className="vendash-review-date">{formatDate(review.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="vendash-products-display-section">
        <div className="vendash-section-header-row">
          <h3 className="vendash-section-title-heading">Recent Products</h3>
          <button className="vendash-view-all-button" onClick={onViewProducts}>
            View All <ChevronRight size={12} />
          </button>
        </div>
        
        {products.length > 0 ? (
          <div className="vendash-products-grid-container">
            {products.slice(0, 4).map(product => {
              const originalImg = product.images && product.images[0] ? product.images[0] : '';
              const thumbSrc = originalImg ? getThumbnailUrl(originalImg, 300, 65) : '';

              return (
                <div key={product.id} className="vendash-product-card-item">
                  <div className="vendash-product-image-wrapper">
                    {thumbSrc ? (
                      <img 
                        src={thumbSrc} 
                        alt={product.title}
                        className="vendash-product-image-display"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          const img = e.currentTarget;
                          if (img.dataset.fallback === 'true') return;
                          img.dataset.fallback = 'true';
                          if (originalImg) img.src = getOriginalUrl(originalImg);
                        }}
                      />
                    ) : (
                      <div className="vendash-product-image-placeholder">
                        <Package size={24} />
                      </div>
                    )}
                    <div className={`vendash-product-badge-status vendash-product-badge-${product.is_active ? 'active' : 'inactive'}`}>
                      {product.is_active ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                  <div className="vendash-product-info-content">
                    <h4 className="vendash-product-title-text">{product.title}</h4>
                    <p className="vendash-product-price-amount">{formatPrice(product.price)}</p>
                    <div className="vendash-product-stats-row">
                      <div className="vendash-product-stat-item">
                        <Eye size={10} />
                        <span>{product.views_count}</span>
                      </div>
                      <div className="vendash-product-stat-item">
                        <Heart size={10} />
                        <span>{product.likes_count}</span>
                      </div>
                      <div className="vendash-product-stat-item">
                        <ShoppingCart size={10} />
                        <span>{product.sales_count}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="vendash-empty-state-container">
            <div className="vendash-empty-icon-container">
              <Package size={24} />
            </div>
            <h4>No Products Yet</h4>
            <p>Start adding products to your shop</p>
            <button className="vendash-cta-primary-button" onClick={onAddProduct}>
              <Plus size={12} /> Add First Product
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AnalyticsTab: React.FC = () => (
  <div className="vendash-analytics-tab">
    <div className="vendash-section-header-row">
      <h3 className="vendash-section-title-heading">Business Analytics</h3>
      <p>Track your shop performance</p>
    </div>
    <div className="vendash-analytics-grid-container">
      <div className="vendash-analytics-card-item">
        <div className="vendash-analytics-icon-container">
          <BarChart2 size={20} />
        </div>
        <h4>Sales Performance</h4>
        <p>Detailed sales analytics</p>
        <div className="vendash-coming-soon-badge">Coming Soon</div>
      </div>
      <div className="vendash-analytics-card-item">
        <div className="vendash-analytics-icon-container">
          <Users size={20} />
        </div>
        <h4>Customer Insights</h4>
        <p>Understand your customers</p>
        <div className="vendash-coming-soon-badge">Coming Soon</div>
      </div>
      <div className="vendash-analytics-card-item">
        <div className="vendash-analytics-icon-container">
          <TrendingUp size={20} />
        </div>
        <h4>Growth Metrics</h4>
        <p>Track growth trends</p>
        <div className="vendash-coming-soon-badge">Coming Soon</div>
      </div>
      <div className="vendash-analytics-card-item">
        <div className="vendash-analytics-icon-container">
          <DollarSign size={20} />
        </div>
        <h4>Revenue Reports</h4>
        <p>Download financial reports</p>
        <div className="vendash-coming-soon-badge">Coming Soon</div>
      </div>
    </div>
  </div>
);

export default VendorDashboard;